import React, { useEffect, useState } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	FormControl,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import { useLazyGetSingleOrganizationQuery } from "../../services/organization";
import { useGetMeQuery } from "../../services/user";
import { config } from "../../config";
import ProgressLoader from "../../components/ProgressLoader/ProgressLoader";

const validationSchema = yup.object().shape({
	name: yup.string().required("Name is required"),
	email: yup.string().required("Email is required").email("Email is invalid"),
	companyName: yup.string().required("Company name is required"),
	phone: yup.string().required("Phone is required"),
	address1: yup.string().required("Address Line 1 is required"),
	cityStateZip: yup.string().required("City, State & Zip is required"),
	mcNumber: yup.string().when("orgType", {
		is: (value) => value !== "dispatcher",
		then: () => yup.string().required("MC Number is required"),
	}),
	dot: yup.string().when("orgType", {
		is: (value) => value !== "dispatcher",
		then: () => yup.string().required(),
	}),
	taxId: yup.string(),
	accountType: yup.string().required("Account Type is required"),
	depositType: yup.string().required("Deposit Type is required"),
	abaRoutingNumber: yup.string().required("ABA Routing Number is required"),
	accountNumber: yup.string().required("Account Number is required"),
});

const Settings = () => {
	const {
		handleSubmit,
		control,
		setValue,
		formState: { errors },
		watch,
	} = useForm({
		resolver: yupResolver(validationSchema),
	});
	const orgType = watch("orgType");

	const [triggerOrg, { data: orgData, isLoading }] = useLazyGetSingleOrganizationQuery();
	const { data: me } = useGetMeQuery();

	const [logo, setLogo] = useState();
	const [voidedCheck, setVoidedCheck] = useState();

	const [updateLoading, setUpdateLoading] = useState(false);

	useEffect(() => {
		if (me) {
			triggerOrg(me.user.organization).unwrap();
		}
	}, [setValue, me]);

	useEffect(() => {
		if (orgData) {
			Object.keys(orgData).forEach((field) => {
				setValue(field, orgData[field]);
			});
			if (orgData.logo && orgData.logo.location) setLogo(orgData.logo.location);
			if (orgData.voidedCheck && orgData.voidedCheck.location)
				setVoidedCheck(orgData.voidedCheck.location);
		}
	}, [orgData]);

	const onSubmit = async (data) => {
		setUpdateLoading(true);
		const formData = new FormData();
		if (data.logo && data.logo instanceof File) {
			formData.append("logo", data.logo);
		}

		if (data.voidedCheck && data.voidedCheck instanceof File) {
			formData.append("voidedCheck", data.voidedCheck);
		}

		Object.keys(data).forEach((key) => {
			if (key !== "logo" && key !== "voidedCheck") {
				formData.append(key, data[key]);
			}
		});

		const accessToken = localStorage.getItem(config.accessTokenName);
		const headers = {
			Authorization: `Bearer ${accessToken}`,
			"Content-Type": "multipart/form-data",
		};

		axios
			.put(`${config.apiBaseUrl}/organizations/update/${orgData._id}`, formData, { headers })
			.then(() => {
				toast.success("Organization details updated successfully!");
				setUpdateLoading(false);
			})
			.catch((error) => {
				console.error("Error updating organization:", error);
				toast.error("Failed to update organization details");
				setUpdateLoading(false);
			});
	};

	if (isLoading) {
		return <ProgressLoader horPosition="start" />;
	}

	return (
		<Card style={{ maxWidth: 800, margin: "0 auto" }}>
			<CardContent>
				<Typography variant="h5" mb={2}>
					Update Organization Details
				</Typography>
				<form onSubmit={handleSubmit(onSubmit)}>
					<Box display="grid" gridTemplateColumns="1fr 1fr" gap="20px">
						{[
							{ name: "name", label: "Name", type: "text" },
							{ name: "email", label: "Email", type: "email" },
							{ name: "companyName", label: "Company Name", type: "text" },
							{ name: "phone", label: "Phone", type: "text" },
							{ name: "address1", label: "Address Line 1", type: "text" },
							{ name: "cityStateZip", label: "City, State & Zip", type: "text" },
							{ name: "taxId", label: "Tax ID", type: "text", required: false },
							{ name: "accountType", label: "Account Type", type: "text" },
							{ name: "depositType", label: "Deposit Type", type: "text" },
							{ name: "abaRoutingNumber", label: "ABA Routing Number", type: "text" },
							{ name: "accountNumber", label: "Account Number", type: "text" },
							...(orgType !== "dispatcher"
								? [
										{ name: "mcNumber", label: "MC#", type: "text" },
										{ name: "dot", label: "DOT", type: "text" },
								  ]
								: []),
						].map((field) => (
							<FormControl key={field.name} fullWidth>
								<label style={{ fontWeight: "bold" }} htmlFor={field.name}>
									{field.label}
								</label>
								<Controller
									name={field.name}
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											label={field.label}
											placeholder={field.label}
											type={field.type === "select" ? "text" : field.type}
											select={field.type === "select"}
											error={!!errors[field.name]}
											helperText={errors[field.name]?.message || " "}
											variant="outlined"
											size="small"
											fullWidth
										>
											{field.options?.map((option) => (
												<MenuItem key={option} value={option}>
													{option}
												</MenuItem>
											))}
										</TextField>
									)}
								/>
							</FormControl>
						))}

						<FormControl
							sx={{
								alignSelf: "center",
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
							}}
						>
							<Controller
								name="logo"
								control={control}
								defaultValue={null}
								render={({ field: { onChange, onBlur, name, ref } }) => (
									<TextField
										type="file"
										onChange={(e) => {
											const file = e.target.files[0];
											if (file) {
												const imgUrl = URL.createObjectURL(file);
												onChange(file);
												setLogo(imgUrl);
											}
										}}
										onBlur={onBlur}
										name={name}
										inputRef={ref}
										InputLabelProps={{ shrink: true }}
										size="small"
										label="Logo"
										variant="outlined"
									/>
								)}
							/>
							{logo && (
								<img src={logo} style={{ width: "100px", borderRadius: "50% 50%" }} alt="logo" />
							)}
						</FormControl>
						<FormControl
							sx={{
								alignSelf: "center",
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
							}}
						>
							<Controller
								name="voidedCheck"
								control={control}
								defaultValue={null}
								render={({ field: { onChange, onBlur, name, ref } }) => (
									<TextField
										type="file"
										onChange={(e) => {
											const file = e.target.files[0];
											if (file) {
												const imgUrl = URL.createObjectURL(file);
												onChange(file);
												setVoidedCheck(imgUrl);
											}
										}}
										onBlur={onBlur}
										name={name}
										inputRef={ref}
										InputLabelProps={{ shrink: true }}
										size="small"
										label="Voided Check"
										variant="outlined"
									/>
								)}
							/>
							{voidedCheck && (
								<img
									src={voidedCheck}
									style={{ width: "100px", borderRadius: "50% 50%" }}
									alt="voided check"
								/>
							)}
						</FormControl>
					</Box>
					<Box mt={3}>
						<Button disabled={updateLoading} type="submit" variant="contained" color="primary">
							Update Details
						</Button>
					</Box>
				</form>
			</CardContent>
		</Card>
	);
};

export default Settings;
